import type { ReactNode } from 'react';
import { BottomTabBar } from './BottomTabBar';
import { SideNav } from './SideNav';
import { TopBar } from './TopBar';
import type { NavItemId } from './nav-items';
import type { User } from '../../types/user';

interface AppLayoutProps {
  active: NavItemId;
  onNavigate: (id: NavItemId) => void;
  user: User;
  /** The cloud-mirror indicator, rendered in the top bar. */
  status?: ReactNode;
  children: ReactNode;
}

/**
 * Application shell: sidebar from `lg:` up, bottom tab bar below it, with the
 * same top bar in both. The screens themselves know nothing about navigation.
 */
export function AppLayout({ active, onNavigate, user, status, children }: AppLayoutProps) {
  return (
    <div className="min-h-screen lg:flex">
      <SideNav active={active} onNavigate={onNavigate} user={user} />

      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar active={active} user={user} status={status} />
        {/* The bottom bar floats over the page, so the last row needs room.
            On paper neither exists, and the sheet gets the whole page.

            `min-w-0 max-w-full`: a flex item measures itself by its content
            unless it is told not to, so one wide screen would otherwise stretch
            this column — and the whole document with it — past the viewport. */}
        <main className="grow min-w-0 max-w-full box-border p-4 sm:p-6 pb-24 lg:pb-6 print:p-0">
          {children}
        </main>
      </div>

      <BottomTabBar active={active} onNavigate={onNavigate} user={user} />
    </div>
  );
}
